import React, { type FC, type ReactNode, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import DragAndDropFile from 'shared/ui/DragAndDropFile/DragAndDropFile'
import { layoutStore } from 'shared/layout'
import { ClickAwayListener, Error } from 'shared/ui'
import { Inbox } from 'entities/Inbox/model/Inbox'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { TextField } from 'widgets/MessageField/ui/TextField/TextField'
import { FieldActions } from 'widgets/MessageField/ui/FieldActions/FieldActions'
import { useMessageFieldContext } from 'widgets/MessageField/context/MessageFieldContext'
import { AttachmentContent } from 'widgets/MessageField/ui/AttachmentContent/AttachmentContent'
import { EditMessageHeader } from 'widgets/MessageField/ui/EditMessageHeader/EditMessageHeader'
import { SwitchMode } from 'widgets/MessageField/ui/SwitchMode/SwitchMode'
import { Alerts } from 'widgets/MessageField/ui/Alerts'
import { DropdownMentions } from 'widgets/MessageField/ui/DropdownMentions/ui/DropdownMentions'
import styles from 'widgets/MessageField/ui/styles.module.scss'
import { useMessageFiledEventAttachmentUploadError } from 'widgets/MessageField/events/useMessageFiledEventAttachmentUploadError'
import { useMessageFiledEventAttachmentUploadSuccessful } from 'widgets/MessageField/events/useMessageFiledEventAttachmentUploadSuccessful'
import { useMessageFieldEventVideoConvert } from 'widgets/MessageField/events/useMessageFieldEventVideoConvert'
import { RecordAudio } from 'widgets/RecordAudio'

export type IMessageFieldContentProps = {
  additionalContent?: ReactNode
  additionalBottomContent?: ReactNode
  alertsContent?: ReactNode
  quietHoursContent?: ReactNode
  onDragHandler?: (condition: boolean) => void
  isCanCollapse?: boolean
  inboxId?: number
  color?: 'yellow'
  uiStyle?: 'new'
  error?: string
}

export const MessageFieldContent: FC<IMessageFieldContentProps> = observer(
  ({
    additionalContent,
    additionalBottomContent,
    alertsContent,
    quietHoursContent,
    onDragHandler,
    isCanCollapse,
    inboxId,
    color,
    uiStyle,
    error,
  }) => {
    const messageFieldStore = useMessageFieldContext()
    const entities = useEntitiesContext()
    const {
      fromSavedReply,
      recordAudioState,
      isVariantNote,
      isVariantConversation,
      isVariantUnderline,
      isVariantDefault,
      isVariantClean,
      isMMSWithAirCallCheck,
      editId,
      disabled,
      activeMessageField,
      hasText,
      alertElement,
      setMessageFieldElement,
      setBlurMessageFieldTrigger,
      isModeNote,
      mentionsStore,
      reactionParentMode,
      loadingSend,
      reset,
      resetOnUnmount,
      recordModalCloseProps,
      resetReactions,
      highlight: storeHighlight,
    } = messageFieldStore

    const ref = useRef<HTMLDivElement>(null)
    const currentInbox = entities.inboxesStore?.currentInbox
    const showMessageField = Boolean(isVariantUnderline || !recordAudioState || fromSavedReply)
    const conversationInboxId = inboxId || entities.conversationStore?.currentItem?.inbox_id
    const conversationInbox = conversationInboxId
      ? entities.inboxesStore?.getItem(conversationInboxId)
      : null

    const highlight = (error && 'error') || storeHighlight

    const isRCS =
      inboxId || currentInbox?.isUnifiedInbox
        ? conversationInbox instanceof Inbox && conversationInbox.isRCS
        : currentInbox instanceof Inbox && currentInbox.isRCS

    const showFieldActions = () => {
      if (isVariantClean) return false

      if (isVariantNote) {
        return activeMessageField || editId || hasText
      }

      if (isVariantUnderline) {
        return !recordAudioState
      }

      return true
    }
    const isDragAndDropEnabled = isVariantConversation || fromSavedReply || isVariantUnderline

    useMessageFieldEventVideoConvert(messageFieldStore)
    useMessageFiledEventAttachmentUploadSuccessful(messageFieldStore)
    useMessageFiledEventAttachmentUploadError(messageFieldStore)

    useEffect(() => {
      reactionParentMode()

      setTimeout(() => {
        if (ref.current) {
          setMessageFieldElement(ref.current)
        }
      })

      return () => {
        resetReactions()
        if (resetOnUnmount) reset()
      }
    }, [])

    const alert = (
      <>
        {alertsContent}
        <Alerts />
      </>
    )

    const handleDrop = (files: File[]) => {
      messageFieldStore.addMediaFiles(files)
    }

    const [isCollapse, setCollapse] = useState(isCanCollapse && layoutStore.isMobileView)

    useEffect(() => {
      if (activeMessageField) {
        setCollapse(false)
      }
    }, [activeMessageField])

    const onClickAway = () => {
      if (isCanCollapse && layoutStore.isMobileView) {
        setCollapse(true)
      }
      setBlurMessageFieldTrigger()
    }

    const contentClasses =
      uiStyle === 'new'
        ? classNames(styles.wrapNew, {
            [styles.active]: activeMessageField,
            [styles.disabled]: disabled,
            [styles[`highlight${highlight}`]]: highlight,
          })
        : classNames(styles.wrap, {
            [styles.active]: activeMessageField || (isVariantNote && (editId || hasText)),
            [styles.disabled]: disabled,
            [styles.isModeNote]: isVariantConversation && isModeNote,
            [styles.isNote]: isVariantNote,
            [styles.isUnderline]: isVariantUnderline,
            [styles.clean]: isVariantClean,
            [styles.default]: !color && (isVariantConversation || isVariantDefault),
            [styles[`highlight${highlight}`]]: highlight,
            [styles.collapse]: isCollapse,
            [styles[`color${color}`]]: color,
          })

    return (
      <ClickAwayListener
        onClickAway={onClickAway}
        mouseEvent='onMouseDown'
        touchEvent='onTouchStart'
      >
        <div className={classNames(styles.flex8, { [styles.noPadding]: uiStyle === 'new' })}>
          {alertElement ? createPortal(alert, alertElement) : isVariantConversation && alert}
          {isVariantConversation && quietHoursContent}
          {(isVariantConversation || isVariantNote) && (
            <DropdownMentions store={mentionsStore} innerRef={ref} />
          )}

          <div className={styles.flex4}>
            <div
              className={contentClasses}
              ref={ref}
              aria-label={disabled ? 'MessageField_disabled' : 'MessageField'}
            >
              {disabled && <div className={classNames(styles.cover, styles.disabled)} />}
              <EditMessageHeader />

              {showMessageField && (
                <>
                  <div
                    className={classNames(styles.wrapFieldContent, {
                      [styles.disabledEvents]: loadingSend,
                      [styles.disabled2]: recordAudioState,
                    })}
                  >
                    {!layoutStore.isMobileView && isVariantConversation && (
                      <SwitchMode
                        toggle={isModeNote}
                        isMMS={isMMSWithAirCallCheck}
                        onChange={messageFieldStore.handleToggleMode}
                        isRCS={isRCS}
                      />
                    )}
                    {!isCollapse && <AttachmentContent />}
                    <div className={styles.wrapTextField}>
                      {isDragAndDropEnabled && (
                        <DragAndDropFile
                          className={classNames({
                            [styles.conversationDragAndDrop]: isVariantConversation,
                            [styles.savedReplyDragAndDrop]: fromSavedReply,
                          })}
                          onDrop={handleDrop}
                          onDragHandler={onDragHandler}
                        />
                      )}
                      <TextField
                        isCollapse={!!isCollapse}
                        className={styles.textarea}
                        classNamePlaceholder={styles.textareaPlaceholder}
                      />
                    </div>
                    {additionalContent}
                  </div>
                  {showFieldActions() && (
                    <div
                      className={classNames(styles.actions, {
                        [styles.disabled2]: recordAudioState,
                      })}
                    >
                      <FieldActions isCollapse={!!isCollapse} isNoPadding={uiStyle === 'new'} />
                    </div>
                  )}
                </>
              )}
              {recordAudioState && (
                <div
                  className={classNames(styles.wrapAudio, {
                    [styles.fromSavedReply]: fromSavedReply,
                  })}
                >
                  <RecordAudio
                    store={messageFieldStore.recordAudioStore}
                    cancelBtnType={fromSavedReply ? 'button' : 'icon'}
                    iconButtonProps={messageFieldStore.iconButtonProps}
                    buttonProps={messageFieldStore.buttonProps}
                    modalCloseProps={recordModalCloseProps}
                  />
                </div>
              )}
            </div>
            {error ? <Error type={'text'} error={error} /> : additionalBottomContent}
          </div>
        </div>
      </ClickAwayListener>
    )
  }
)
